// ── fillBatchIngest — ordering rules for a batch of Kalshi fills (#769) ──
//
// Fill payloads reach the store in an order this module does not get to choose,
// and the two lanes differ in how much we know about it:
//
//   /api/trader/fills  (org key mode)      NEWEST-FIRST — verified,
//                                          `orderBy(desc(fills.executedAt))` in
//                                          `relay/src/services/traderFills.ts`.
//   /api/kalshi/fills  (personal key mode) UNVERIFIED — the relay proxies
//                                          `client.getFills()` and sets no
//                                          ordering parameter, so the order is
//                                          the venue's and nothing here has
//                                          observed it.
//
// That asymmetry is the reason retention is made order-INDEPENDENT rather than
// taught one payload's shape: the guarantee then holds on the lane we measured
// and on the lane we did not.
//
// `parseFillsSnapshot` preserves whatever order arrives, and
// `accountStore.processFill` prepended one row at a time and re-sliced — so a
// newest-first batch built the retained list BACKWARDS, and once the batch
// exceeded the retention cap the newest fills were evicted before any component
// could see them:
//
//   120 fills, newest-first, cap 100  ->  top row rendered 119h old,
//                                         the 0h fill dropped entirely.
//
// That is the >40h Recent Fills rail in the report. No downstream sort could
// recover it, because the rows were already gone from the store.
//
// This module is deliberately free of Zustand, React, the current time and any
// side effect — it takes arrays and returns arrays, so the ordering and
// identity rules are testable as plain data. It is the ordering half only:
// dedup-with-sounds, placement confirmations and position refreshes stay in
// `processFill`, which owns them.

import type { KalshiFill } from '@/types/kalshi';

/**
 * Sort key for a fill.
 *
 * An unparseable timestamp sorts to the OLD end rather than being dropped or
 * read as "now": a row the tape cannot place is still a fill the trader made,
 * and defaulting it to the current instant would float it above real activity.
 * Same rule, and the same reasoning, as `recentFillTape.toMillis`.
 *
 * Not reachable through `parseFillsSnapshot` today — `isUsableKalshiFillRow`
 * rejects a whole payload containing an unparseable `created_at` — so this is a
 * module invariant that keeps ingestion from crashing, not a live case.
 */
function fillTimeMs(fill: KalshiFill): number {
  const ms = Date.parse(fill.createdAt);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

/**
 * Compare two fills newest-first.
 *
 * Equal instants are common — one order filling in a single sweep — so the
 * tie-break is on `fillId` and must be total and independent of input order,
 * or the rail would reshuffle between polls that carried the same rows in a
 * different sequence.
 */
function byNewestFirst(a: KalshiFill, b: KalshiFill): number {
  const at = fillTimeMs(a);
  const bt = fillTimeMs(b);
  if (at !== bt) return bt - at;
  return a.fillId < b.fillId ? 1 : a.fillId > b.fillId ? -1 : 0;
}

/**
 * The retained Kalshi slice: deduplicated, newest-first, capped.
 *
 * Applied AFTER a batch has been ingested, so the store's invariant holds
 * whatever order the payload arrived in — including the case
 * `orderFillBatchForIngest` alone cannot cover, where the list already held a
 * fill newer than anything in the new batch.
 *
 * First occurrence of a `fillId` wins, matching `processFill`'s own
 * "first sighting is the one we keep" dedup.
 */
export function retainNewestFills(fills: readonly KalshiFill[], cap: number): KalshiFill[] {
  const byId = new Map<string, KalshiFill>();
  for (const fill of fills) {
    if (!byId.has(fill.fillId)) byId.set(fill.fillId, fill);
  }
  return [...byId.values()].sort(byNewestFirst).slice(0, Math.max(0, cap));
}
